import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, TextField, Stack } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Using TextField with datetime-local input instead of MUI DateTimePicker
import { GlobalContext } from '../../../context/context';
import { handleRunTransitScan, TransitScanState, TransitScanActions } from '../handlers/transit';
import { logEvent } from '../../../services/analytics';

interface TransitScanModalProps {
  open: boolean;
  onClose: () => void;
  global: GlobalContext;
  state: TransitScanState;
  actions: TransitScanActions;
}

const TransitScanModal: React.FC<TransitScanModalProps> = ({
  open,
  onClose,
  global,
  state,
  actions
}) => {
  const { t } = useTranslation(['map', 'common']);
  const [distance, setDistance] = useState<number>(2);
  const [arrivalTime, setArrivalTime] = useState<Date>(new Date());
  const { loading, point, cache } = state;
  const { setLoading, setProgress, setIsModalOpen } = actions;

  // Track modal opening
  useEffect(() => {
    if (open && point) {
      logEvent({
        category: 'Transit Analysis',
        action: 'TransitScanModalOpened',
        label: point.name || 'Unnamed Point',
        point_id: point.id,
        point_coordinates: `${point.coordinates[0]},${point.coordinates[1]}`
      });
    }
  }, [open, point]);

  const handleDistanceChange = (newDistance: number) => {
    logEvent({
      category: 'Transit Analysis',
      action: 'TransitScanDistanceChanged',
      label: point?.name || 'Unnamed Point',
      point_id: point?.id,
      old_distance: distance,
      new_distance: newDistance
    });
    setDistance(newDistance);
  };

  const handleArrivalTimeChange = (newTime: Date | null) => {
    if (newTime) {
      logEvent({
        category: 'Transit Analysis',
        action: 'TransitScanArrivalTimeChanged',
        label: point?.name || 'Unnamed Point',
        point_id: point?.id,
        old_time: arrivalTime.toISOString(),
        new_time: newTime.toISOString()
      });
      setArrivalTime(newTime);
    }
  };

  const handleRun = () => {
    if (distance > 0 && arrivalTime && point) {
      logEvent({
        category: 'Transit Analysis',
        action: 'TransitScanInitiated',
        label: point.name || 'Unnamed Point',
        point_id: point.id,
        distance_km: distance,
        arrival_time: arrivalTime.toISOString(),
        cache_size: cache.size
      });

      handleRunTransitScan({
        point,
        distanceKm: distance,
        arrivalTime,
        global,
        transitTimeCache: cache,
        setTransitScanLoading: setLoading,
        setTransitScanProgress: setProgress,
        setIsTransitScanModalOpen: setIsModalOpen
      });
    }
  };

  const handleClose = () => {
    logEvent({
      category: 'Transit Analysis',
      action: 'TransitScanModalClosed',
      label: point?.name || 'Unnamed Point',
      point_id: point?.id,
      final_distance: distance,
      final_arrival_time: arrivalTime.toISOString()
    });
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
      <DialogTitle>{t('modals.transitScan.title')}</DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 2 }}>
          <TextField
            label={t('modals.transitScan.distance')}
            type="number"
            value={distance}
            onChange={e => handleDistanceChange(Number(e.target.value))}
            inputProps={{ min: 0.1, step: 0.1 }}
            fullWidth
          />
          <TextField
            label={t('modals.transitScan.arrivalTime')}
            type="datetime-local"
            value={arrivalTime ? arrivalTime.toISOString().slice(0, 16) : ''}
            onChange={(event) => {
              const dateValue = event.target.value;
              if (dateValue) {
                handleArrivalTimeChange(new Date(dateValue));
              }
            }}
            fullWidth
            InputLabelProps={{
              shrink: true,
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={loading}>{t('common:cancel')}</Button>
        <Button onClick={handleRun} variant="contained" disabled={loading || distance <= 0 || !arrivalTime || !point}>
          {loading ? t('modals.transitScan.running') : t('modals.transitScan.run')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TransitScanModal; 